import axios from "axios";

export interface FileUploadResponse {
  fileUrl: string;
  fileId: string;
  signedUrl?: string;
}

export interface FileUploadParams {
  file: File;
  refCollectionId: string;
  refCollectionName: string;
  refCollectionField: string;
  refCollectionFieldType: "string" | "array";
}

export class FileService {
  /**
   * Upload a file to the backend API
   * @param params - File upload parameters
   * @returns Promise with file upload response
   */
  static async uploadFile(
    params: FileUploadParams,
  ): Promise<FileUploadResponse> {
    try {

      const formData = new FormData();
      formData.append("file", params.file);
      formData.append("refCollectionId", params.refCollectionId);
      formData.append("refCollectionName", params.refCollectionName);
      formData.append("refCollectionField", params.refCollectionField);
      formData.append("refCollectionFieldType", params.refCollectionFieldType);

      const response = await axios.post(
        `${process.env.REACT_APP_API_V4}/api/file`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const result = response.data;

      return {
        fileUrl: result.fileUrl,
        fileId: result.fileId,
        signedUrl: result.signedUrl,
      };
    } catch (error: any) {
      console.error("File upload error:", error);
      
      // Handle file too large error
      if (error.response?.status === 413) {
        const errorData = error.response.data;
        throw new Error(errorData.error || "Arquivo muito grande. O tamanho máximo permitido é 15MB.");
      }
      
      // Generic error handling
      const errorMessage = error.response?.data?.error || error.message || "Falha ao fazer upload do arquivo";
      throw new Error(errorMessage);
    }
  }

  /**
   * Extract text from a file using the backend API
   * @param file - The file to extract text from
   * @returns Promise with extracted text
   */
  static async extractText(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append("file", file);

			const endpoint = `${process.env.REACT_APP_API_V4}/api/file/extract-text`;

      const response = await axios.post(
        endpoint,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const result = response.data;
      return result.text || result.extractedText || "";
    } catch (error: any) {
      console.error("File text extraction error:", error);
      
      // Handle file too large error
      if (error.response?.status === 413) {
        const errorData = error.response.data;
        throw new Error(errorData.error || "Arquivo muito grande. O tamanho máximo permitido é 15MB.");
      }
      
      // Generic error handling
      const errorMessage = error.response?.data?.error || error.message || "Falha ao extrair texto do arquivo";
      throw new Error(errorMessage);
    }
  }

  /**
   * Helper method specifically for uploading profile photos
   * @param file - The image file to upload
   * @param registrationId - The registration ID to associate the photo with
   * @returns Promise with file upload response
   */
  static async uploadProfilePhoto(
    file: File,
    registrationId: string,
  ): Promise<FileUploadResponse> {
    return this.uploadFile({
      file,
      refCollectionId: registrationId,
      refCollectionName: "registrations",
      refCollectionField: "profile.photoId",
      refCollectionFieldType: "string",
    });
  }

  static async deleteFileById(id: string) {
    const response = await axios.delete(`${process.env.REACT_APP_API_V4}/api/file/${id}`);
    return response.data;
  }
}
